import { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import * as RequestCodeStyles from './RequestCode.styles';
import * as authStyles from '../../Auth.styles';
import PhoneNumberInput from '../../../PhoneNumberInput';
import ProceedButton from '../../../ProceedButton';
import { AccountField, SignUpFormData } from '../../../../utils/forms';
import { AuthContextFields } from '../../../../utils/authFormState';
import { FormTextFieldProps } from '../../../FormTextField/FormTextField';
import { submittingAuth$ } from '../../../../store/submitting/selectors';

interface RequestCodeProps {
  requestCodeButtonText?: string;
  /**
   * If this component is used as part of a sign-up (inside a <form />) or as sign-in (login form).
   */
  embed?: boolean;
  mobileTextFieldProps?: Partial<FormTextFieldProps>;
}
interface ContentsProps extends RequestCodeProps {
  handleOnSubmit?: (values: AuthContextFields) => void;
}

let nudgeReset: ReturnType<typeof setTimeout> | undefined;

function RequestCode({ requestCodeButtonText = 'Next', embed, handleOnSubmit, mobileTextFieldProps }: ContentsProps) {
  const { classes: shakeClasses } = RequestCodeStyles.useShakeStyles();
  const { classes: buttonClasses } = authStyles.useButton();

  const submitting = useSelector(submittingAuth$);

  const [nudge, setNudge] = useState(false);

  const { values, errors, submitCount, isSubmitting, setFieldTouched, touched } = useFormikContext<
    AuthContextFields | SignUpFormData
  >();

  const submitAttempts = useRef(submitCount);

  const hasError = errors[AccountField.MOBILE] && touched[AccountField.MOBILE];

  // Animate (nudge) the the button if the user attempts to submit the whole sign-up without requesting a code.
  useEffect(() => {
    if (submitCount > submitAttempts.current) {
      submitAttempts.current = submitCount;
      setNudge(true);
      nudgeReset = setTimeout(() => setNudge(false), 820);
    }

    return () => {
      if (nudgeReset) clearTimeout(nudgeReset);
    };
  }, [submitCount]);

  const submit = () => {
    setFieldTouched(AccountField.MOBILE, true);

    if (handleOnSubmit && !errors[AccountField.MOBILE] && values[AccountField.MOBILE]) {
      setFieldTouched(AccountField.VERIFICATION_CODE, false);

      handleOnSubmit({
        [AccountField.MOBILE]: values[AccountField.MOBILE],
        [AccountField.VERIFICATION_CODE]: values[AccountField.VERIFICATION_CODE],
      });
    }
  };

  return (
    <>
      <PhoneNumberInput
        mobileTextFieldProps={mobileTextFieldProps}
        submitting={submitting}
        formikField={AccountField.MOBILE}
      />

      <ProceedButton
        classes={buttonClasses}
        className={nudge && !isSubmitting ? shakeClasses.runShake : ''}
        fullWidth
        disabled={hasError || submitting}
        loading={submitting}
        type={embed ? 'button' : 'submit'}
        {...(handleOnSubmit ? { onClick: submit } : {})}
      >
        {requestCodeButtonText}
      </ProceedButton>
    </>
  );
}

export default RequestCode;
